import {
  supabase,
} from '@/lib/supabase';

import {
  readCanonicalTreeRetirementReadinessInstallationIdentity,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessPseudonymousInstallationIdentity';

import type {
  CanonicalTreeRetirementReadinessClientEvidenceKind,
  CanonicalTreeRetirementReadinessPublicationFailureClassification,
  CanonicalTreeRetirementReadinessPublicationInput,
  CanonicalTreeRetirementReadinessPublicationResult,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidencePublicationArchitecture';

export type CanonicalTreeRetirementReadinessPublicationOptions =
  Readonly<{
    signal?:
      AbortSignal;

    retryBaseDelayMs?:
      number;
  }>;

type CanonicalTreeRetirementReadinessRpcResponse =
  Readonly<{
    accepted:
      true;

    duplicate:
      boolean;

    evidenceId:
      string;

    receivedAt:
      string;

    retirementAuthorized:
      false;
  }>;

const PUBLICATION_RPC =
  'record_tree_retirement_readiness_evidence';

const LIFECYCLE_SCHEMA_VERSION =
  2 as const;

const MAX_TOTAL_ATTEMPTS =
  2;

const DEFAULT_RETRY_BASE_DELAY_MS =
  250;

const MINIMUM_RETRY_BASE_DELAY_MS =
  25;

const MAXIMUM_RETRY_BASE_DELAY_MS =
  2_000;

const INELIGIBLE_INSTALLATION_PERSISTENCE =
  new Set([
    'MEMORY_ONLY',
    'UNAVAILABLE',
  ] as const);

const CLIENT_EVENT_KINDS =
  new Set<CanonicalTreeRetirementReadinessClientEvidenceKind>([
    'CLIENT_OBSERVED',
    'PROJECTION_AVAILABLE',
    'FALLBACK_ACTIVATED',
    'RENDER_FAILURE',
    'MIGRATION_COMPLETED',
    'CORRUPTION_RECOVERED',
    'OPEN_EPISODE_OBSERVED',
    'OPEN_EPISODES_CLEARED',
    'CONSUMER_DEPENDENCY_OBSERVED',
  ]);

const GOVERNED_PROOF_KINDS =
  new Set([
    'STATIC_CONSUMER_ABSENCE_PROVEN',
    'RUNTIME_CONSUMER_ABSENCE_PROVEN',
    'ARCHIVAL_COMPLETED',
    'ROLLBACK_PROOF_COMPLETED',
  ]);

const VALID_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const inFlightByCanonicalSnapshot =
  new Map<
    string,
    Promise<CanonicalTreeRetirementReadinessPublicationResult>
  >();

const inFlightByIdempotencyKey =
  new Map<
    string,
    Promise<CanonicalTreeRetirementReadinessPublicationResult>
  >();

const skipped =
  (
    reason:
      | 'AUTHENTICATION_REQUIRED'
      | 'PERSISTED_INSTALLATION_IDENTITY_REQUIRED'
      | 'GOVERNED_PROOF_EVENT_REJECTED',
  ): CanonicalTreeRetirementReadinessPublicationResult =>
    Object.freeze({
      status:
        'SKIPPED',

      accepted:
        false,

      reason,

      retryable:
        false,

      retirementAuthorized:
        false,
    });

const failed =
  (
    failureClassification:
      CanonicalTreeRetirementReadinessPublicationFailureClassification,

    retryable:
      boolean,

    retryAttempt:
      number,
  ): CanonicalTreeRetirementReadinessPublicationResult =>
    Object.freeze({
      status:
        'FAILED',

      accepted:
        false,

      failureClassification,

      retryable,

      retryAttempt,

      retirementAuthorized:
        false,
    });

const clampRetryDelay =
  (
    value:
      number | undefined,
  ): number => {
    if (
      typeof value !==
        'number' ||
      !Number.isFinite(
        value,
      )
    ) {
      return DEFAULT_RETRY_BASE_DELAY_MS;
    }

    return Math.min(
      MAXIMUM_RETRY_BASE_DELAY_MS,
      Math.max(
        MINIMUM_RETRY_BASE_DELAY_MS,
        Math.floor(
          value,
        ),
      ),
    );
  };

const normalizeClientObservedAt =
  (
    value:
      string | null,
  ):
    | string
    | null
    | undefined => {
    if (
      value ===
      null
    ) {
      return null;
    }

    if (
      typeof value !==
        'string' ||
      value.length <
        20 ||
      value.length >
        40
    ) {
      return undefined;
    }

    const epoch =
      Date.parse(
        value,
      );

    if (
      !Number.isFinite(
        epoch,
      )
    ) {
      return undefined;
    }

    return new Date(
      epoch,
    ).toISOString();
  };

const validateInput =
  (
    input:
      CanonicalTreeRetirementReadinessPublicationInput,
  ):
    | Readonly<{
        ok:
          true;

        normalizedClientObservedAt:
          string | null;
      }>
    | Readonly<{
        ok:
          false;

        governedProofEvent:
          boolean;
      }> => {
    const candidate =
      input as unknown as
        Record<
          string,
          unknown
        >;

    const eventKind =
      candidate.eventKind;

    if (
      typeof eventKind ===
        'string' &&
      GOVERNED_PROOF_KINDS.has(
        eventKind,
      )
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          true,
      });
    }

    if (
      typeof eventKind !==
        'string' ||
      !CLIENT_EVENT_KINDS.has(
        eventKind as CanonicalTreeRetirementReadinessClientEvidenceKind,
      )
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    if (
      typeof candidate.buildId !==
        'string' ||
      candidate.buildId.trim().length <
        1 ||
      candidate.buildId.trim().length >
        200
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    if (
      typeof candidate.deploymentId !==
        'string' ||
      candidate.deploymentId.trim().length <
        1 ||
      candidate.deploymentId.trim().length >
        200
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    if (
      candidate.lifecycleSchemaVersion !==
      LIFECYCLE_SCHEMA_VERSION
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    if (
      typeof candidate.openEpisodeCount !==
        'number' ||
      !Number.isInteger(
        candidate.openEpisodeCount,
      ) ||
      candidate.openEpisodeCount <
        0 ||
      candidate.openEpisodeCount >
        64
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    if (
      typeof candidate.migrationCompleted !==
        'boolean' ||
      typeof candidate.quarantinePresent !==
        'boolean'
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    const normalizedClientObservedAt =
      normalizeClientObservedAt(
        candidate.clientObservedAt as
          string | null,
      );

    if (
      normalizedClientObservedAt ===
      undefined
    ) {
      return Object.freeze({
        ok:
          false,

        governedProofEvent:
          false,
      });
    }

    return Object.freeze({
      ok:
        true,

      normalizedClientObservedAt,
    });
  };

const encodeHex =
  (
    bytes:
      ArrayBuffer,
  ): string =>
    Array.from(
      new Uint8Array(
        bytes,
      ),
    )
      .map(
        value =>
          value
            .toString(
              16,
            )
            .padStart(
              2,
              '0',
            ),
      )
      .join(
        '',
      );

const createCanonicalPublicationSnapshot =
  (
    sessionUserId:
      string,

    installationId:
      string,

    input:
      CanonicalTreeRetirementReadinessPublicationInput,

    normalizedClientObservedAt:
      string | null,
  ): string =>
    JSON.stringify([
      'TREE_RETIREMENT_READINESS_V2',
      sessionUserId,
      installationId,
      input.eventKind,
      input.buildId.trim(),
      input.deploymentId.trim(),
      input.lifecycleSchemaVersion,
      input.openEpisodeCount,
      input.migrationCompleted,
      input.quarantinePresent,
      normalizedClientObservedAt,
    ]);

const createIdempotencyKey =
  async (
    canonicalSnapshot:
      string,
  ): Promise<string | null> => {
    const cryptoAuthority =
      globalThis.crypto;

    if (
      !cryptoAuthority ||
      !cryptoAuthority.subtle ||
      typeof cryptoAuthority.subtle.digest !==
        'function'
    ) {
      return null;
    }

    const digest =
      await cryptoAuthority.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(
          canonicalSnapshot,
        ),
      );

    return `tree-v2-${encodeHex(
      digest,
    )}`;
  };

const classifyRpcFailure =
  (
    error:
      unknown,
  ): CanonicalTreeRetirementReadinessPublicationFailureClassification => {
    const candidate =
      error as
        | Readonly<{
            code?:
              unknown;

            message?:
              unknown;

            details?:
              unknown;

            hint?:
              unknown;
          }>
        | null;

    const raw =
      [
        candidate?.code,
        candidate?.message,
        candidate?.details,
        candidate?.hint,
      ]
        .filter(
          value =>
            typeof value ===
            'string',
        )
        .join(
          ' ',
        )
        .toUpperCase();

    if (
      raw.includes(
        'INSTALLATION_RATE_LIMIT_EXCEEDED',
      )
    ) {
      return 'INSTALLATION_RATE_LIMIT_EXCEEDED';
    }

    if (
      raw.includes(
        'AUTHENTICATION_REQUIRED',
      ) ||
      raw.includes(
        'JWT',
      ) ||
      raw.includes(
        '28000',
      )
    ) {
      return 'AUTHENTICATION_REQUIRED';
    }

    if (
      raw.includes(
        'GOVERNED_PROOF_EVENT_REQUIRES_SERVER_AUTHORITY',
      )
    ) {
      return 'GOVERNED_PROOF_EVENT_REJECTED';
    }

    if (
      raw.includes(
        'INVALID_',
      ) ||
      raw.includes(
        'UNKNOWN_EVENT_KIND',
      ) ||
      raw.includes(
        'UNSUPPORTED_LIFECYCLE_SCHEMA',
      ) ||
      raw.includes(
        'CLIENT_TIMESTAMP_OUT_OF_RANGE',
      ) ||
      raw.includes(
        'PROOF_PROVENANCE_NOT_ACCEPTED_FROM_CLIENT',
      ) ||
      raw.includes(
        '22023',
      )
    ) {
      return 'INVALID_PUBLICATION_INPUT';
    }

    if (
      /FAILED TO FETCH|NETWORK|OFFLINE|CONNECTION|ECONN|TIMEOUT|TIMED OUT/.test(
        raw,
      )
    ) {
      return 'NETWORK_FAILURE';
    }

    if (
      raw.length >
      0
    ) {
      return 'SERVER_FAILURE';
    }

    return 'UNKNOWN_FAILURE';
  };

const isRetryableFailure =
  (
    classification:
      CanonicalTreeRetirementReadinessPublicationFailureClassification,
  ): boolean =>
    classification ===
      'NETWORK_FAILURE' ||
    classification ===
      'SERVER_FAILURE';

const waitForRetry =
  async (
    baseDelayMs:
      number,

    attempt:
      number,

    idempotencyKey:
      string,

    signal:
      AbortSignal | undefined,
  ): Promise<boolean> => {
    if (
      signal?.aborted
    ) {
      return false;
    }

    const jitterSeed =
      Number.parseInt(
        idempotencyKey.slice(
          -4,
        ),
        16,
      );

    const jitter =
      Number.isFinite(
        jitterSeed,
      )
        ? jitterSeed %
          Math.max(
            1,
            Math.floor(
              baseDelayMs /
                2,
            ),
          )
        : 0;

    const delay =
      Math.min(
        MAXIMUM_RETRY_BASE_DELAY_MS,
        baseDelayMs *
          2 ** Math.max(
            0,
            attempt -
              1,
          ) +
          jitter,
      );

    return new Promise<boolean>(
      resolve => {
        let settled =
          false;

        const finish =
          (
            value:
              boolean,
          ) => {
            if (
              settled
            ) {
              return;
            }

            settled =
              true;

            signal?.removeEventListener(
              'abort',
              onAbort,
            );

            resolve(
              value,
            );
          };

        const timer =
          globalThis.setTimeout(
            () =>
              finish(
                true,
              ),
            delay,
          );

        const onAbort =
          () => {
            globalThis.clearTimeout(
              timer,
            );

            finish(
              false,
            );
          };

        signal?.addEventListener(
          'abort',
          onAbort,
          {
            once:
              true,
          },
        );
      },
    );
  };

const isValidRpcResponse =
  (
    value:
      unknown,
  ): value is CanonicalTreeRetirementReadinessRpcResponse => {
    if (
      !value ||
      typeof value !==
        'object'
    ) {
      return false;
    }

    const candidate =
      value as
        Record<
          string,
          unknown
        >;

    return (
      candidate.accepted ===
        true &&
      typeof candidate.duplicate ===
        'boolean' &&
      typeof candidate.evidenceId ===
        'string' &&
      VALID_UUID.test(
        candidate.evidenceId,
      ) &&
      typeof candidate.receivedAt ===
        'string' &&
      Number.isFinite(
        Date.parse(
          candidate.receivedAt,
        ),
      ) &&
      candidate.retirementAuthorized ===
        false
    );
  };

const executePublication =
  async (
    input:
      CanonicalTreeRetirementReadinessPublicationInput,

    normalizedClientObservedAt:
      string | null,

    initialSessionUserId:
      string,

    installationId:
      string,

    idempotencyKey:
      string,

    options:
      CanonicalTreeRetirementReadinessPublicationOptions,
  ): Promise<CanonicalTreeRetirementReadinessPublicationResult> => {
    const retryBaseDelayMs =
      clampRetryDelay(
        options.retryBaseDelayMs,
      );

    for (
      let attempt =
        1;
      attempt <=
      MAX_TOTAL_ATTEMPTS;
      attempt +=
        1
    ) {
      if (
        options.signal?.aborted
      ) {
        return failed(
          'ABORTED',
          false,
          attempt,
        );
      }

      const {
        data:
          sessionData,

        error:
          sessionError,
      } =
        await supabase.auth.getSession();

      const session =
        sessionData.session;

      if (
        sessionError ||
        !session ||
        !session.user ||
        session.user.id !==
          initialSessionUserId
      ) {
        return skipped(
          'AUTHENTICATION_REQUIRED',
        );
      }

      const identity =
        readCanonicalTreeRetirementReadinessInstallationIdentity();

      if (
        INELIGIBLE_INSTALLATION_PERSISTENCE.has(
          identity.persistence as
            'MEMORY_ONLY' | 'UNAVAILABLE',
        ) ||
        identity.persistence !==
          'PERSISTED' ||
        identity.installationId !==
          installationId
      ) {
        return skipped(
          'PERSISTED_INSTALLATION_IDENTITY_REQUIRED',
        );
      }

      try {
        const rpcBuilder =
          supabase.rpc(
            PUBLICATION_RPC,
            {
              p_installation_id:
                installationId,

              p_idempotency_key:
                idempotencyKey,

              p_event_kind:
                input.eventKind,

              p_build_id:
                input.buildId.trim(),

              p_deployment_id:
                input.deploymentId.trim(),

              p_lifecycle_schema_version:
                LIFECYCLE_SCHEMA_VERSION,

              p_open_episode_count:
                input.openEpisodeCount,

              p_migration_completed:
                input.migrationCompleted,

              p_quarantine_present:
                input.quarantinePresent,

              p_client_observed_at:
                normalizedClientObservedAt,

              p_proof_provenance:
                null,
            },
          );

        const {
          data,
          error,
        } =
          options.signal
            ? await rpcBuilder.abortSignal(
                options.signal,
              )
            : await rpcBuilder;

        if (
          options.signal?.aborted
        ) {
          return failed(
            'ABORTED',
            false,
            attempt,
          );
        }

        if (
          !error &&
          isValidRpcResponse(
            data,
          )
        ) {
          if (
            data.duplicate
          ) {
            return Object.freeze({
              status:
                'DUPLICATE_ACCEPTED',

              accepted:
                true,

              duplicate:
                true,

              evidenceId:
                data.evidenceId,

              receivedAt:
                data.receivedAt,

              installationId,

              retirementAuthorized:
                false,
            });
          }

          return Object.freeze({
            status:
              'ACCEPTED',

            accepted:
              true,

            duplicate:
              false,

            evidenceId:
              data.evidenceId,

            receivedAt:
              data.receivedAt,

            installationId,

            retirementAuthorized:
              false,
          });
        }

        const classification =
          error
            ? classifyRpcFailure(
                error,
              )
            : 'SERVER_FAILURE';

        const retryable =
          isRetryableFailure(
            classification,
          );

        if (
          !retryable ||
          attempt >=
            MAX_TOTAL_ATTEMPTS
        ) {
          return failed(
            classification,
            retryable,
            attempt,
          );
        }
      } catch (
        unknownError:
          unknown
      ) {
        if (
          options.signal?.aborted
        ) {
          return failed(
            'ABORTED',
            false,
            attempt,
          );
        }

        const classification =
          classifyRpcFailure(
            unknownError,
          );

        const retryable =
          isRetryableFailure(
            classification,
          );

        if (
          !retryable ||
          attempt >=
            MAX_TOTAL_ATTEMPTS
        ) {
          return failed(
            classification,
            retryable,
            attempt,
          );
        }
      }

      const continueAfterDelay =
        await waitForRetry(
          retryBaseDelayMs,
          attempt,
          idempotencyKey,
          options.signal,
        );

      if (
        !continueAfterDelay
      ) {
        return failed(
          'ABORTED',
          false,
          attempt,
        );
      }
    }

    return failed(
      'UNKNOWN_FAILURE',
      false,
      MAX_TOTAL_ATTEMPTS,
    );
  };

export const publishCanonicalTreeRetirementReadinessEvidence =
  async (
    input:
      CanonicalTreeRetirementReadinessPublicationInput,

    options:
      CanonicalTreeRetirementReadinessPublicationOptions =
        {},
  ): Promise<CanonicalTreeRetirementReadinessPublicationResult> => {
    try {
      if (
        options.signal?.aborted
      ) {
        return failed(
          'ABORTED',
          false,
          0,
        );
      }

      const validation =
        validateInput(
          input,
        );

      if (
        !validation.ok
      ) {
        return validation.governedProofEvent
          ? skipped(
              'GOVERNED_PROOF_EVENT_REJECTED',
            )
          : failed(
              'INVALID_PUBLICATION_INPUT',
              false,
              0,
            );
      }

      const {
        data:
          sessionData,

        error:
          sessionError,
      } =
        await supabase.auth.getSession();

      const session =
        sessionData.session;

      if (
        sessionError ||
        !session ||
        !session.user
      ) {
        return skipped(
          'AUTHENTICATION_REQUIRED',
        );
      }

      const identity =
        readCanonicalTreeRetirementReadinessInstallationIdentity();

      if (
        INELIGIBLE_INSTALLATION_PERSISTENCE.has(
          identity.persistence as
            'MEMORY_ONLY' | 'UNAVAILABLE',
        ) ||
        identity.persistence !==
          'PERSISTED' ||
        typeof identity.installationId !==
          'string' ||
        !VALID_UUID.test(
          identity.installationId,
        )
      ) {
        return skipped(
          'PERSISTED_INSTALLATION_IDENTITY_REQUIRED',
        );
      }

      const canonicalSnapshot =
        createCanonicalPublicationSnapshot(
          session.user.id,
          identity.installationId,
          input,
          validation.normalizedClientObservedAt,
        );

      const existingCanonicalPublication =
        inFlightByCanonicalSnapshot.get(
          canonicalSnapshot,
        );

      if (
        existingCanonicalPublication
      ) {
        return existingCanonicalPublication;
      }

      const canonicalPublication =
        (async () => {
          const idempotencyKey =
            await createIdempotencyKey(
              canonicalSnapshot,
            );

          if (
            !idempotencyKey ||
            idempotencyKey.length <
              16 ||
            idempotencyKey.length >
              200
          ) {
            return failed(
              'INVALID_PUBLICATION_INPUT',
              false,
              0,
            );
          }

          const existingIdempotentPublication =
            inFlightByIdempotencyKey.get(
              idempotencyKey,
            );

          if (
            existingIdempotentPublication
          ) {
            return existingIdempotentPublication;
          }

          const publication =
            executePublication(
              input,
              validation.normalizedClientObservedAt,
              session.user.id,
              identity.installationId,
              idempotencyKey,
              options,
            );

          inFlightByIdempotencyKey.set(
            idempotencyKey,
            publication,
          );

          try {
            return await publication;
          } finally {
            if (
              inFlightByIdempotencyKey.get(
                idempotencyKey,
              ) ===
              publication
            ) {
              inFlightByIdempotencyKey.delete(
                idempotencyKey,
              );
            }
          }
        })();

      inFlightByCanonicalSnapshot.set(
        canonicalSnapshot,
        canonicalPublication,
      );

      try {
        return await canonicalPublication;
      } finally {
        if (
          inFlightByCanonicalSnapshot.get(
            canonicalSnapshot,
          ) ===
          canonicalPublication
        ) {
          inFlightByCanonicalSnapshot.delete(
            canonicalSnapshot,
          );
        }
      }
    } catch {
      return failed(
        'UNKNOWN_FAILURE',
        false,
        0,
      );
    }
  };

export const clearCanonicalTreeRetirementReadinessEvidencePublicationForTest =
  (): void => {
    inFlightByCanonicalSnapshot.clear();
    inFlightByIdempotencyKey.clear();
  };

export const describeCanonicalTreeRetirementReadinessEvidencePublication =
  () =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-096',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PUBLICATION_IMPLEMENTATION',

      decision:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PUBLICATION_IMPLEMENTED',

      predecessorMilestone:
        'CC-TREE-ARCH-095',

      publicationServiceCreated:
        true,

      publicationRpc:
        PUBLICATION_RPC,

      trustedSessionResolution:
        'supabase.auth.getSession()',

      serverOwnerIdentityAuthority:
        'auth.uid()',

      installationIdentitySource:
        'readCanonicalTreeRetirementReadinessInstallationIdentity',

      persistedInstallationIdentityRequired:
        true,

      ineligibleInstallationPersistence:
        Object.freeze([
          'MEMORY_ONLY',
          'UNAVAILABLE',
        ]),

      memoryOnlyInstallationIdentityMayPublish:
        false,

      unavailableInstallationIdentityMayPublish:
        false,

      deterministicSha256Idempotency:
        true,

      preDigestCanonicalSnapshotDeduplication:
        true,

      concurrentDeduplicationMaterial:
        'ACCOUNT_INSTALLATION_EVENT_SNAPSHOT',

      inProcessConcurrentDeduplication:
        true,

      maximumTotalAttempts:
        MAX_TOTAL_ATTEMPTS,

      retryPolicy:
        'BOUNDED_EXPONENTIAL_BACKOFF_WITH_JITTER',

      publicationMode:
        'BEST_EFFORT_NON_BLOCKING',

      proofProvenanceSubmitted:
        null,

      ownerUserIdSubmitted:
        false,

      privateFamilyDataSubmitted:
        false,

      readinessProjectionRead:
        false,

      directTableCrudPerformed:
        false,

      authContextModified:
        false,

      treeWorkspaceWired:
        false,

      commandCenterWired:
        false,

      runtimeDecisionDependencyAdded:
        false,

      runtimeGuardDependencyAdded:
        false,

      retirementAuthorized:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      nextGovernedDependency:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PUBLICATION_CERTIFICATION',
    } as const);
